/**
 * PURE. Presets (human-unit field values) → quantized DNA fields; species/look default DNAs (creatures §A2.2, §A7.4).
 */
import type { CreatureDNA } from '../../shared';
import { DYE_IDS, FUR_PALETTE_IDS, PARTS, TINCTURE_IDS, optIndex } from '../../content/creature/parts';
import { BASE_PRESET, SPECIES_BY_ID, speciesAt, type LookPreset, type SpeciesDef } from '../../content/creature/species';
import { DNA_FIELDS, FIELD_BY_NAME, PROMOTED_FIELDS, fieldMax, sliderMid, sliderQ, type FieldDef } from './fields';

/** Mutable field map (every §A12.1 field incl. the promoted ones, as integers). */
export type FieldMap = Record<string, number>;

/** One preset value → the field's integer. */
export function presetValue(f: FieldDef, v: string | number): number {
  let out: number;
  if (typeof v === 'number') {
    out = f.kind === 'slider' ? sliderQ(f, v) : v;
  } else if (f.kind === 'enum') {
    out = optIndex(f.slot!, v);
  } else if (f.kind === 'palette') {
    out = (FUR_PALETTE_IDS as readonly string[]).indexOf(v);
  } else if (f.kind === 'dye') {
    out = (DYE_IDS as readonly string[]).indexOf(v);
  } else if (f.kind === 'tincture') {
    out = (TINCTURE_IDS as readonly string[]).indexOf(v);
  } else {
    throw new Error(`field ${f.name}: string preset value '${v}' not supported`);
  }
  if (!(out >= 0 && out <= fieldMax(f))) throw new Error(`field ${f.name}: preset value ${String(v)} → ${out} out of range`);
  return out;
}

/** Applies a preset onto a field map (unknown field names throw — presets are checked content). */
export function applyPreset(fields: FieldMap, preset: LookPreset): FieldMap {
  for (const [k, v] of Object.entries(preset)) {
    const f = FIELD_BY_NAME.get(k);
    if (!f) throw new Error(`preset: unknown field ${k}`);
    fields[k] = presetValue(f, v);
  }
  return fields;
}

/** Every field at its neutral value: sliders at `2^(b−1)`, enums at 0, then {@link BASE_PRESET}. */
export function neutralFields(): FieldMap {
  const m: FieldMap = {};
  for (const f of DNA_FIELDS) m[f.name] = f.kind === 'slider' ? sliderMid(f) : 0;
  m.ver = 1;
  return applyPreset(m, BASE_PRESET);
}

/** Groups whose fields come from the HEAD species in a chimera (headwear stays with the body's look). */
const HEAD_GROUPS = new Set(['head', 'eyes', 'face']);
const isHeadField = (name: string): boolean => {
  const f = FIELD_BY_NAME.get(name);
  return !!f && HEAD_GROUPS.has(f.group) && name !== 'headwear';
};

/** Default fields of a species (+ look), optionally with another head species (creatures §A2.1 «Голова другого зверя»). */
export function speciesFields(body: SpeciesDef | string, opts: { head?: SpeciesDef | string; look?: number } = {}): FieldMap {
  const b = typeof body === 'string' ? SPECIES_BY_ID.get(body) ?? speciesAt(0) : body;
  const h = opts.head === undefined ? b : typeof opts.head === 'string' ? SPECIES_BY_ID.get(opts.head) ?? b : opts.head;
  const look = Math.max(0, Math.min(b.looks.length - 1, opts.look ?? 0));
  const m = neutralFields();
  const bodyPreset: Record<string, string | number> = { ...b.defaults, ...b.looks[look].preset };
  if (h !== b) {
    // Head fields come from the head species' default look; body keeps everything else.
    for (const k of Object.keys(bodyPreset)) if (isHeadField(k)) delete bodyPreset[k];
    const headPreset: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(h.defaults)) if (isHeadField(k)) headPreset[k] = v;
    applyPreset(m, headPreset);
  }
  applyPreset(m, bodyPreset);
  m.bodySpecies = b.index;
  m.headSpecies = h.index;
  m.look = look;
  return m;
}

/** Builds a `CreatureDNA` from a full field map (name resolved by the caller via `resolveName`). */
export function dnaFromFields(fields: FieldMap, name: string, soulSeed?: number): CreatureDNA {
  const rest: Record<string, number> = {};
  for (const f of DNA_FIELDS) if (!PROMOTED_FIELDS.has(f.name)) rest[f.name] = fields[f.name] ?? 0;
  return {
    v: 1,
    bodySpecies: speciesAt(fields.bodySpecies ?? 0).id,
    headSpecies: speciesAt(fields.headSpecies ?? 0).id,
    pronoun: (fields.pronoun ?? 0) ? 1 : 0,
    name,
    soulSeed: (soulSeed ?? fields.soulSeed ?? 0) & 0xffff,
    fields: rest,
  };
}

/** The full integer field map of a DNA (promoted members folded back in). */
export function fieldsOf(d: CreatureDNA): FieldMap {
  const m: FieldMap = {};
  for (const f of DNA_FIELDS) m[f.name] = f.kind === 'slider' ? sliderMid(f) : 0;
  for (const [k, v] of Object.entries(d.fields)) if (FIELD_BY_NAME.has(k)) m[k] = v;
  m.ver = 1;
  m.bodySpecies = SPECIES_BY_ID.get(d.bodySpecies)?.index ?? 0;
  m.headSpecies = SPECIES_BY_ID.get(d.headSpecies)?.index ?? 0;
  m.pronoun = d.pronoun;
  m.soulSeed = d.soulSeed & 0xffff;
  return m;
}

/** Number of options of an enum slot (for range checks / randomizer). */
export const slotSize = (slot: string): number => (PARTS as Record<string, readonly unknown[]>)[slot]?.length ?? 0;
